import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Bell, User } from 'lucide-react';
import { usersApi } from '../../services/api';
import Avatar from '../shared/Avatar';
import ActivityLog from '../activity/ActivityLog';
import toast from 'react-hot-toast';

export default function Settings() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
  });

  const user = meData?.data;

  const photoMutation = useMutation({
    mutationFn: (file: File) => usersApi.uploadPhoto(user!.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Foto atualizada!');
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      {/* Profile section */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <User size={16} className="text-brand-500" />
          <h2 className="font-semibold text-gray-800">Perfil</h2>
        </div>
        {user && (
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <Avatar name={user.name} photoUrl={user.photo_url} size="lg" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={photoMutation.isPending}
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-brand-500 text-white rounded-full flex items-center justify-center hover:bg-brand-600 transition-colors shadow-sm"
                aria-label="Alterar foto"
              >
                <Camera size={12} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) photoMutation.mutate(f);
                }}
              />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 truncate">{user.name}</p>
              <p className="text-sm text-gray-500">{user.role_label}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Push notifications info */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} className="text-brand-500" />
          <h2 className="font-semibold text-gray-800">Notificações</h2>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Instale o app na tela inicial do seu celular para receber notificações de entrevistas,
          tarefas atrasadas e lembretes de reunião.
        </p>
        <button
          onClick={() => {
            if ('Notification' in window) {
              Notification.requestPermission().then(perm => {
                if (perm === 'granted') {
                  toast.success('Notificações ativadas!');
                } else {
                  toast.error('Permissão negada. Verifique as configurações do navegador.');
                }
              });
            } else {
              toast.error('Notificações não disponíveis neste dispositivo.');
            }
          }}
          className="btn-secondary text-sm"
        >
          Ativar notificações push
        </button>
      </div>

      {/* About */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-3">Sobre o App</h2>
        <p className="text-sm text-gray-600">Presidência da Estaca Sorocaba Brasil Barcelona</p>
        <p className="text-xs text-gray-400 mt-1">
          Igreja de Jesus Cristo dos Santos dos Últimos Dias
        </p>
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">Versão 1.0.0</p>
          <p className="text-xs text-gray-300 mt-1">© 2026 — Todos os direitos reservados</p>
          <p className="text-xs text-gray-300 mt-0.5">Desenvolvido por Marcus Cortez</p>
        </div>
      </div>

      {/* Activity logs */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-4">Logs de Atividade</h2>
        <ActivityLog />
      </div>
    </div>
  );
}
